import React, { useState, useEffect } from 'react';
import { bool, func, object, number, string } from 'prop-types';
import classNames from 'classnames';

import { FormattedMessage, intlShape } from '../../../../util/reactIntl';
import { ACCOUNT_SETTINGS_PAGES } from '../../../../routing/routeConfiguration';
import { propTypes } from '../../../../util/types';
import {
  Avatar,
  InlineTextButton,
  LinkedLogo,
  Menu,
  MenuLabel,
  MenuContent,
  MenuItem,
  NamedLink,
} from '../../../../components';

import TopbarSearchForm from '../TopbarSearchForm/TopbarSearchForm';
import Icons from '../../../../components/Icons/Icons';
import { getUserType } from '../../../../util/userDataExtraction';
import { topbarNavSubMenu, UserType } from '../../../../util/enums';
import RenderNavMenu from './RenderNavMenu';
import CustomLinksMenu from './CustomLinksMenu/CustomLinksMenu';

import css from './TopbarDesktop.module.css';

const SignupLink = () => {
  return (
    <NamedLink name="SignupPage" className={classNames(css.navLink, css.loginLink)}>
      <FormattedMessage id="TopbarDesktop.signup" />
    </NamedLink>
  );
};

const LoginLink = () => {
  return (
    <NamedLink name="LoginPage" className={classNames(css.navLink, css.createLink)}>
      <FormattedMessage id="TopbarDesktop.login" />
    </NamedLink>
  );
};

const InboxLink = ({ notificationCount, currentUserHasListings }) => {
  const notificationDot = notificationCount > 0 ? <div className={css.notificationDot} /> : null;
  return (
    <NamedLink
      className={css.navLink}
      name="OrdersListViewPage"
      // params={{ tab: currentUserHasListings ? 'sales' : 'orders' }}
    >
      <span className={css.inbox}>
        <FormattedMessage id="TopbarDesktop.inbox" />
        {/* {notificationDot} */}
      </span>
    </NamedLink>
  );
};

const MessagesLink = () => {
  return (
    <NamedLink className={css.navLink} name="MessagesBasePage">
      <FormattedMessage id="TopbarDesktop.messages" />
    </NamedLink>
  );
};

const ProfileMenu = ({ currentPage, currentUser, onLogout }) => {
  const userType = getUserType(currentUser);
  const currentPageClass = page => {
    const isAccountSettingsPage =
      page === 'AccountSettingsPage' && ACCOUNT_SETTINGS_PAGES.includes(currentPage);
    return currentPage === page || isAccountSettingsPage ? css.currentPage : null;
  };

  return (
    <Menu>
      <MenuLabel className={css.profileMenuLabel} isOpenClassName={css.profileMenuIsOpen}>
        <Avatar className={css.avatar} user={currentUser} disableProfileLink />
      </MenuLabel>
      <MenuContent className={css.profileMenuContent}>
        <MenuItem key="FavoritesPage">
          <NamedLink
            className={classNames(css.menuLink, currentPageClass('FavoritesPage'))}
            name="FavoritesPage"
          >
            <FormattedMessage id="TopbarDesktop.FavoritesPage" />
          </NamedLink>
        </MenuItem>
        <MenuItem key="AccountSettingsPage">
          {userType !== UserType.TALENT && (
            <NamedLink
              className={classNames(css.menuLink, currentPageClass('AccountSettingsPage'))}
              name="AccountSettingsPage"
            >
              <FormattedMessage id="TopbarDesktop.accountSettingsLink" />
            </NamedLink>
          )}
        </MenuItem>
        <MenuItem key="logout">
          <InlineTextButton rootClassName={css.logoutButton} onClick={onLogout}>
            <FormattedMessage id="TopbarDesktop.logout" />
          </InlineTextButton>
        </MenuItem>
      </MenuContent>
    </Menu>
  );
};

const TopbarDesktop = props => {
  const {
    className,
    config,
    customLinks,
    currentUser,
    currentPage,
    rootClassName,
    currentUserHasListings,
    notificationCount,
    intl,
    isAuthenticated,
    onLogout,
    onSearchSubmit,
    initialSearchFormValues,
    currentUserListing,
  } = props;
  const userType = getUserType(currentUser);

  const [mounted, setMounted] = useState(false);
  const [isSearchToggle, setSearchToggle] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const marketplaceName = config.marketplaceName;
  const authenticatedOnClientSide = mounted && isAuthenticated;
  const isAuthenticatedOrJustHydrated = isAuthenticated || !mounted;

  const giveSpaceForSearch = customLinks == null || customLinks?.length === 0;
  const classes = classNames(rootClassName || css.root, className);

  const inboxLinkMaybe = authenticatedOnClientSide ? (
    <InboxLink
      notificationCount={notificationCount}
      currentUserHasListings={currentUserHasListings}
    />
  ) : null;
  const messagesLinkMaybe = authenticatedOnClientSide ? <MessagesLink /> : null;

  const profileMenuMaybe = authenticatedOnClientSide ? (
    <ProfileMenu currentPage={currentPage} currentUser={currentUser} onLogout={onLogout} />
  ) : null;

  const signupLinkMaybe = isAuthenticatedOrJustHydrated ? null : <SignupLink />;
  const loginLinkMaybe = isAuthenticatedOrJustHydrated ? null : <LoginLink />;

  const search = (
    <div className={css.searchExpanded}>
      <TopbarSearchForm
        className={classNames(css.searchLink, { [css.takeAvailableSpace]: giveSpaceForSearch })}
        onSubmit={onSearchSubmit}
        initialValues={initialSearchFormValues}
        appConfig={config}
      />
      <span className={css.crossIcon} onClick={() => setSearchToggle(!isSearchToggle)}>
        <Icons name="cross" />
      </span>
    </div>
  );

  return (
    <nav className={classes}>
      {isSearchToggle ? (
        search
      ) : (
        <div className={css.contentWidth}>
          <div className={css.leftLinks}>
            <LinkedLogo
              className={css.logoLink}
              layout="desktop"
              alt={intl.formatMessage({ id: 'TopbarDesktop.logo' }, { marketplaceName })}
              linkToExternalSite={config?.topbar?.logoLink}
            />
            <NamedLink className={css.navLink} name="LandingPage">
              <FormattedMessage id="TopbarDesktop.home" />
            </NamedLink>
            <NamedLink className={css.navLink} name="CMSPage" params={{ pageId: 'member-pathway' }}>
              <FormattedMessage id="TopbarDesktop.thePathway" />
            </NamedLink>
            <NamedLink
              className={css.navLink}
              name="CMSPage"
              params={{ pageId: 'the-edge-zone-system' }}
            >
              <FormattedMessage id="TopbarDesktop.edgeZone" />
            </NamedLink>
            <RenderNavMenu menuOptions={topbarNavSubMenu} />
          </div>
          <div className={css.rightLinks}>
            {isAuthenticated && ![UserType.TALENT, UserType.FAN].includes(userType) ? (
              <NamedLink
                className={css.navLink}
                key="BrandsOnboardingPage"
                name="BrandsOnboardingPage"
              >
                <FormattedMessage id="TopbarDesktop.BrandsOnboardingPage" />
              </NamedLink>
            ) : null}
            <CustomLinksMenu
              currentPage={currentPage}
              customLinks={customLinks}
              intl={intl}
              hasClientSideContentReady={
                authenticatedOnClientSide || !isAuthenticatedOrJustHydrated
              }
              customClassName={css.navLink}
              currentUserListing={currentUserListing}
              userType={userType}
            />
            {isAuthenticated && userType !== UserType.FAN ? (
              <NamedLink className={css.navLink} key="subscription" name="SubscriptionPage">
                <FormattedMessage id="TopbarDesktop.SubscriptionPage" />
              </NamedLink>
            ) : null}
            {messagesLinkMaybe}
            {inboxLinkMaybe}
            {profileMenuMaybe}
            {signupLinkMaybe}
            {loginLinkMaybe}
          </div>
        </div>
      )}
    </nav>
  );
};

TopbarDesktop.defaultProps = {
  rootClassName: null,
  className: null,
  currentUser: null,
  currentPage: null,
  notificationCount: 0,
  initialSearchFormValues: {},
  config: null,
};

TopbarDesktop.propTypes = {
  rootClassName: string,
  className: string,
  currentUserHasListings: bool.isRequired,
  currentUser: propTypes.currentUser,
  currentPage: string,
  isAuthenticated: bool.isRequired,
  onLogout: func.isRequired,
  notificationCount: number,
  onSearchSubmit: func.isRequired,
  initialSearchFormValues: object,
  intl: intlShape.isRequired,
  config: object,
};

export default TopbarDesktop;
